import { FC, useContext, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import PageWrapper from '../../../layout/PageWrapper/PageWrapper';
import Page from '../../../layout/Page/Page';
import FormGroup from '../../../components/bootstrap/forms/FormGroup';
import Input from '../../../components/bootstrap/forms/Input';
import Button from '../../../components/bootstrap/Button';
import Logo from '../../../components/Logo';
import useDarkMode from '../../../hooks/useDarkMode';
import AuthContext from '../../../contexts/authContext';
import Spinner from '../../../components/bootstrap/Spinner';
import { useFormik } from 'formik';
import { loginForStaff } from '../../../services/login.service';
import { toasts } from '../../../services/toaste.service';
import { getLicenseKey } from '../../../services/application.settings';
import InputGroup, { InputGroupText } from '../../../components/bootstrap/forms/InputGroup';
import Icon from '../../../components/icon/Icon';
import TmtBg from '../../../assets/img/tmt_bg.png';
import { getConfigDetails } from '../../../services/common.service';

interface ILoginProps {
	isSignUp?: boolean;
}

const Login: FC<ILoginProps> = ({ isSignUp }) => {

	const { setUserAccountId, setUserTypeId, setConfigData, configData } = useContext(AuthContext);

	const { darkModeStatus } = useDarkMode();
	const navigate = useNavigate();
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [showPassword, setShowPassword] = useState<boolean>(false);
	const [apiError, setApiError] = useState<string | null>(null);

	useEffect(() => {
		if (apiError) {
			const timeout = setTimeout(() => {
				setApiError(null);
			}, 5000);
			return () => clearTimeout(timeout);
		}
	}, [apiError]);


	const staffLoginForm = useFormik({
		enableReinitialize: true,
		initialValues: {
			emailOrMobile: '',
			password: '',
		},
		validate: (values) => {
			const errors: { emailOrMobile?: string; password?: string } = {};

			if (!values.emailOrMobile) {
				errors.emailOrMobile = 'Required';
			} else if (
				!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.emailOrMobile)
			) {
				errors.emailOrMobile = 'Invalid email address';
			}

			if (!values.password) {
				errors.password = 'Required';
			}

			return errors;
		},

		onSubmit: (values) => { staffLogin() },
	});

	function setLogin() {
		return {
			emailOrMobile: staffLoginForm.values.emailOrMobile,
			password: staffLoginForm.values.password
		}
	}

	const clientName = configData?.find((item: any) => item.configName === 'CLIENT_NAME')?.configValue || '';


	function staffLogin() {
		setIsLoading(true);
		setTimeout(() => {
			if (staffLoginForm.isValid) {
				let loginData = setLogin();
				loginForStaff(loginData,
					(response) => {
						const data = response.data;
						if (data.success == true) {
							toasts(data.message, "Success");
							let loginData = response.data.data.loginWithPassword;
							localStorage.setItem('userAccountId', loginData.userAccountId);
							localStorage.setItem('userTypeId', loginData.userTypeId);

							if (setUserAccountId) {
								setUserAccountId(loginData.userAccountId);
							}
							if (setUserTypeId) {
								setUserTypeId(loginData.userTypeId);
							}							
							setIsLoading(false);
							let screenPath = loginData.screenPath
							navigate(`../${screenPath}`)
						}
						else if (data.success == false) {
							setApiError(data.message);
							setIsLoading(false);
						}
						else {
							let errorCode = response.data.error[0].error_code;
							let errorDescription = response.data.error[0].error_description;
							setApiError(errorDescription);
							setIsLoading(false);
						}
					}
					, (error) => {
						toasts(error, "Error")
					}
				)
			} else if (staffLoginForm.isValid == false) {
				setApiError('Please fill all the details!');
				setIsLoading(false);
			}
		}, 1000);
	}

	return (
		<PageWrapper
			isProtected={false}
			title='Login'
			className={classNames('p-0 bg-white')}>
			<Page className='p-0 m-0 overflow-hidden' container='fluid'>
				<div className='row g-0 h-100'>
					<div
						className='col-lg-7 d-none d-lg-flex align-items-center justify-content-center'
						style={{
							backgroundImage: `url(${TmtBg})`,
							backgroundSize: 'cover',
							backgroundPosition: 'center',
							position: 'relative'
						}}>
						<div
							style={{
								position: 'absolute',
								top: 0,
								left: 0,
								right: 0,
								bottom: 0,
								background: 'linear-gradient(135deg, rgba(15, 12, 41, 0.8) 0%, rgba(48, 43, 99, 0.4) 100%)',
								zIndex: 1
							}}
						/>
						<div className='text-white text-center p-5 position-relative' style={{ zIndex: 2 }}>
							<Logo width={200} height={200} />
							<div className='display-3 fw-bold mt-4 tracking-tighter'>{clientName}</div>
							<div className='h4 text-light mt-3 opacity-75'>Quality Yarn. Textile Excellence. Innovation.</div>
						</div>
					</div>

					{/* Right Panel: Login Form */}
					<div className='col-lg-5 col-12 d-flex align-items-center justify-content-center bg-white shadow-lg' style={{ zIndex: 10 }}>
						<div className='w-100 px-5' style={{ maxWidth: '500px' }}>
							<div className='mb-5'>
								<div className='display-4 fw-bold'>Sign In</div>
								<div className='text-muted small'>Please enter your credentials to continue.</div>
							</div>

							<form className='row g-4' onSubmit={staffLoginForm.handleSubmit}>
								<div className="col-12">
									<FormGroup id="emailOrMobile" isFloating label="Email">
										<Input
											autoComplete="emailOrMobile"
											value={staffLoginForm.values.emailOrMobile}
											isTouched={staffLoginForm.touched.emailOrMobile}
											invalidFeedback={staffLoginForm.errors.emailOrMobile}
											isValid={staffLoginForm.touched.emailOrMobile ? !staffLoginForm.errors.emailOrMobile : undefined}
											onChange={staffLoginForm.handleChange}
											onBlur={staffLoginForm.handleBlur}
											onFocus={() => {
												staffLoginForm.setErrors({});
												setApiError(null);
											}}
										/>
									</FormGroup>
								</div>

								<div className="col-12">
									<InputGroup>
										<FormGroup
											id="password"
											isFloating
											label="Password"
											className='flex-grow-1'>
											<Input
												id="password"
												placeholder="Enter Password"
												autoComplete="current-password"
												value={staffLoginForm.values.password}
												isTouched={staffLoginForm.touched.password}
												invalidFeedback={staffLoginForm.errors.password}
												isValid={staffLoginForm.isValid}
												onChange={staffLoginForm.handleChange}
												onBlur={staffLoginForm.handleBlur}
												onFocus={() => {
													staffLoginForm.setErrors({});
													setApiError(null);
												}}
												type={showPassword ? 'text' : 'password'}
											/>
										</FormGroup>
										<InputGroupText
											className={`border ${staffLoginForm.touched.password && staffLoginForm.errors.password ? 'border-danger' : ''}`}
											style={{ height: 'calc(3.5rem + 2px)', alignSelf: 'flex-start' }}>
											<Icon
												icon={showPassword ? 'Visibility' : 'VisibilityOff'}
												className='cursor-pointer'
												size='lg'
												onClick={() => setShowPassword(!showPassword)}
											/>
										</InputGroupText>
									</InputGroup>
								</div>

								<div className='col-12 mt-4'>
									{apiError && (
										<div className='text-danger text-center mb-4 fw-bold'>
											{apiError}
										</div>
									)}
									<Button
										color='primary'
										size='lg'
										type='submit'
										className='w-100 py-3 shadow-sm fw-bold'
										hoverShadow='lg'
										rounded='pill'
										icon='Login'
										isDisable={!staffLoginForm.isValid}
									>
										{isLoading ? (
											<Spinner isSmall inButton isGrow />
										) : (
											"Login"
										)}
									</Button>
								</div>
							</form>
						</div>
					</div>
				</div>
			</Page>
		</PageWrapper >
	);
};
// Login.propTypes = {
// 	isSignUp: PropTypes.bool,
// };
// Login.defaultProps = {
// 	isSignUp: false,
// };

export default Login;
